import React, { useState } from 'react';
import { PageHeader } from '../components/dashboard/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Activity, Play, Clock, Megaphone, ShieldAlert, Crosshair, TrendingDown, AlertTriangle, Users, MessageSquare, Sparkles } from 'lucide-react';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/Select';
import { Label } from '../components/Label';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/Dialog';
const scenarios = [{
  id: 's1',
  name: 'Pemerintah Diam 24 Jam',
  icon: Clock,
  color: 'slate'
}, {
  id: 's2',
  name: 'Statement Presiden Langsung',
  icon: Megaphone,
  color: 'primary'
}, {
  id: 's3',
  name: 'Tindakan Kominfo Takedown',
  icon: ShieldAlert,
  color: 'amber'
}, {
  id: 's4',
  name: 'Eskalasi ke Aparat Keamanan',
  icon: Crosshair,
  color: 'red'
}];
const outcomes: Record<string, {
  sentiment: string;
  sentimentNum: number;
  escalation: number;
  risk: string;
  regions: string[];
  reach: string;
  platforms: string[];
  score: number;
  aiNote: string;
}> = {
  s1: {
    sentiment: '−25%',
    sentimentNum: -25,
    escalation: 85,
    risk: 'TINGGI',
    regions: ['Jakarta', 'Surabaya', 'Medan'],
    reach: '18.7M',
    platforms: ['TikTok', 'X', 'WhatsApp'],
    score: 32,
    aiNote: 'Risiko krisis informasi nasional. Narasi oposisi akan mendominasi 80% ruang digital dalam 24 jam.'
  },
  s2: {
    sentiment: '+15%',
    sentimentNum: 15,
    escalation: 30,
    risk: 'RENDAH',
    regions: ['Jakarta'],
    reach: '24.2M',
    platforms: ['TV Nasional', 'YouTube', 'X'],
    score: 78,
    aiNote: 'Efektif meredam kepanikan dalam 4 jam pertama. Skor strategis tertinggi.'
  },
  s3: {
    sentiment: '+5%',
    sentimentNum: 5,
    escalation: 45,
    risk: 'SEDANG',
    regions: ['Jakarta', 'Bandung'],
    reach: '8.4M',
    platforms: ['TikTok', 'IG', 'FB'],
    score: 64,
    aiNote: 'Takedown dapat memicu narasi "pemerintah anti-kritik"; perlu literasi paralel.'
  },
  s4: {
    sentiment: '−18%',
    sentimentNum: -18,
    escalation: 64,
    risk: 'TINGGI',
    regions: ['Jakarta', 'Papua', 'Sulawesi'],
    reach: '14.1M',
    platforms: ['X', 'TikTok', 'FB'],
    score: 41,
    aiNote: 'Berisiko membesarkan isu menjadi krisis HAM. Hanya gunakan jika ancaman fisik nyata.'
  }
};
const comparisonRows = [{
  id: 's1',
  name: 'Pemerintah Diam 24 Jam'
}, {
  id: 's2',
  name: 'Statement Presiden Langsung'
}, {
  id: 's3',
  name: 'Tindakan Kominfo Takedown'
}, {
  id: 's4',
  name: 'Eskalasi ke Aparat Keamanan'
}];
export function CrisisSimulation() {
  const [activeScenario, setActiveScenario] = useState('s2');
  const [issue, setIssue] = useState('Hoaks Bansos Pangan');
  const [duration, setDuration] = useState('24 jam');
  const [startLevel, setStartLevel] = useState('YELLOW');
  const [compareOpen, setCompareOpen] = useState(false);
  const handleRun = () => {
    toast.success('Simulasi berjalan...', {
      description: `Skenario: ${scenarios.find(s => s.id === activeScenario)?.name}`
    });
  };
  const outcome = outcomes[activeScenario];
  return <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-6 pb-10" data-id="element-1454">
      <PageHeader group="Komando & Respons" title="Crisis Simulation Engine" description="Simulator strategis what-if untuk memprediksi konsekuensi keputusan dalam berbagai skenario krisis nasional." icon={Activity} data-id="element-1455" />

      {/* ROW 1: Scenario Selector */}
      <Card className="bg-card border-border/50" data-id="element-1456">
        <CardHeader className="pb-3 pt-4 px-5 border-b border-border/50" data-id="element-1457">
          <CardTitle className="font-mono text-sm uppercase tracking-wider" data-id="element-1458">
            Pilih Skenario Krisis
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5" data-id="element-1459">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-1460">
            {scenarios.map(s => {
            const active = activeScenario === s.id;
            return <button key={s.id} onClick={() => setActiveScenario(s.id)} className={`text-left p-4 rounded-lg border transition ${active ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 'border-border/50 bg-muted/10 hover:border-primary/40 hover:bg-muted/30'}`} data-id="element-1461">
                  <s.icon className={`size-5 mb-3 ${active ? 'text-primary' : 'text-muted-foreground'}`} data-id="element-1462" />
                  <p className={`text-sm font-bold ${active ? 'text-primary' : 'text-foreground'}`} data-id="element-1463">
                    {s.name}
                  </p>
                </button>;
          })}
          </div>
        </CardContent>
      </Card>

      {/* ROW 2: Simulation Inputs */}
      <Card className="bg-card border-border/50" data-id="element-1464">
        <CardContent className="p-5 grid grid-cols-1 md:grid-cols-4 gap-4 items-end" data-id="element-1465">
          <div data-id="element-1466">
            <Label className="text-xs font-mono uppercase text-muted-foreground" data-id="element-1467">
              Isu Pemicu
            </Label>
            <Select value={issue} onValueChange={setIssue} data-id="element-1468">
              <SelectTrigger className="mt-1.5" data-id="element-1469">
                <SelectValue data-id="element-1470" />
              </SelectTrigger>
              <SelectContent data-id="element-1471">
                <SelectItem value="Hoaks Bansos Pangan" data-id="element-1472">
                  Hoaks Bansos Pangan
                </SelectItem>
                <SelectItem value="Demo Buruh UMP" data-id="element-1473">Demo Buruh UMP</SelectItem>
                <SelectItem value="Deepfake Audio Presiden" data-id="element-1474">
                  Deepfake Audio Presiden
                </SelectItem>
                <SelectItem value="Krisis BBM" data-id="element-1475">Krisis BBM</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div data-id="element-1476">
            <Label className="text-xs font-mono uppercase text-muted-foreground" data-id="element-1477">
              Durasi Simulasi
            </Label>
            <Select value={duration} onValueChange={setDuration} data-id="element-1478">
              <SelectTrigger className="mt-1.5" data-id="element-1479">
                <SelectValue data-id="element-1480" />
              </SelectTrigger>
              <SelectContent data-id="element-1481">
                <SelectItem value="12 jam" data-id="element-1482">12 jam</SelectItem>
                <SelectItem value="24 jam" data-id="element-1483">24 jam</SelectItem>
                <SelectItem value="48 jam" data-id="element-1484">48 jam</SelectItem>
                <SelectItem value="7 hari" data-id="element-1485">7 hari</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div data-id="element-1486">
            <Label className="text-xs font-mono uppercase text-muted-foreground" data-id="element-1487">
              Threat Level Awal
            </Label>
            <Select value={startLevel} onValueChange={setStartLevel} data-id="element-1488">
              <SelectTrigger className="mt-1.5" data-id="element-1489">
                <SelectValue data-id="element-1490" />
              </SelectTrigger>
              <SelectContent data-id="element-1491">
                <SelectItem value="GREEN" data-id="element-1492">GREEN</SelectItem>
                <SelectItem value="YELLOW" data-id="element-1493">YELLOW</SelectItem>
                <SelectItem value="ORANGE" data-id="element-1494">ORANGE</SelectItem>
                <SelectItem value="RED" data-id="element-1495">RED</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleRun} className="w-full" data-id="element-1496">
            <Play className="size-3.5 mr-2" data-id="element-1497" /> Jalankan Simulasi
          </Button>
        </CardContent>
      </Card>

      {/* ROW 3: Predicted Outcomes */}
      <div className="grid grid-cols-12 gap-4" data-id="element-1498">
        <Card className="col-span-12 md:col-span-6 lg:col-span-3 bg-card border-border/50" data-id="element-1499">
          <CardContent className="p-5" data-id="element-1500">
            <div className="flex items-center gap-2 mb-2" data-id="element-1501">
              <TrendingDown className="size-4 text-red-500" data-id="element-1502" />
              <p className="font-mono text-[10px] uppercase text-muted-foreground" data-id="element-1503">
                Sentiment Drop
              </p>
            </div>
            <p className={`font-mono text-3xl font-bold ${outcome.sentimentNum < 0 ? 'text-red-500' : 'text-emerald-500'}`} data-id="element-1504">
              {outcome.sentiment}
            </p>
            <svg viewBox="0 0 100 30" className="w-full h-8 mt-3" data-id="element-1505">
              <polyline points="0,8 15,12 30,10 45,18 60,16 75,22 100,26" fill="none" stroke={outcome.sentimentNum < 0 ? '#ef4444' : '#10b981'} strokeWidth="2" data-id="element-1506" />
            </svg>
            <p className="font-mono text-[9px] text-muted-foreground mt-1" data-id="element-1507">
              Baseline 24j: 48% positif
            </p>
          </CardContent>
        </Card>

        <Card className="col-span-12 md:col-span-6 lg:col-span-3 bg-card border-border/50" data-id="element-1508">
          <CardContent className="p-5" data-id="element-1509">
            <div className="flex items-center gap-2 mb-2" data-id="element-1510">
              <AlertTriangle className="size-4 text-amber-500" data-id="element-1511" />
              <p className="font-mono text-[10px] uppercase text-muted-foreground" data-id="element-1512">
                Probabilitas Eskalasi RED
              </p>
            </div>
            <p className={`font-mono text-3xl font-bold ${outcome.escalation >= 60 ? 'text-red-500' : outcome.escalation >= 40 ? 'text-amber-500' : 'text-emerald-500'}`} data-id="element-1513">
              {outcome.escalation}%
            </p>
            <div className="relative size-16 mx-auto mt-2" data-id="element-1514">
              <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90" data-id="element-1515">
                <circle cx="18" cy="18" r="14" fill="none" stroke="hsl(var(--muted))" strokeWidth="3" data-id="element-1516" />
                <circle cx="18" cy="18" r="14" fill="none" stroke={outcome.escalation >= 60 ? '#ef4444' : '#f59e0b'} strokeWidth="3" strokeDasharray={`${outcome.escalation * 0.88} 100`} strokeLinecap="round" data-id="element-1517" />
              </svg>
            </div>
          </CardContent>
        </Card>

        <Card className="col-span-12 md:col-span-6 lg:col-span-3 bg-card border-border/50" data-id="element-1518">
          <CardContent className="p-5" data-id="element-1519">
            <div className="flex items-center gap-2 mb-2" data-id="element-1520">
              <Users className="size-4 text-amber-500" data-id="element-1521" />
              <p className="font-mono text-[10px] uppercase text-muted-foreground" data-id="element-1522">
                Risiko Mobilisasi Massa
              </p>
            </div>
            <Badge variant="outline" className={`font-mono text-[10px] h-5 ${outcome.risk === 'TINGGI' ? 'bg-red-500/10 text-red-500 border-red-500/30' : outcome.risk === 'SEDANG' ? 'bg-amber-500/10 text-amber-500 border-amber-500/30' : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30'}`} data-id="element-1523">
              {outcome.risk}
            </Badge>
            <p className="font-mono text-[9px] uppercase text-muted-foreground mt-3 mb-1" data-id="element-1524">
              Wilayah Terdampak
            </p>
            <div className="flex flex-wrap gap-1" data-id="element-1525">
              {outcome.regions.map(r => <span key={r} className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-muted/50 border border-border/50" data-id="element-1526">
                  {r}
                </span>)}
            </div>
          </CardContent>
        </Card>

        <Card className="col-span-12 md:col-span-6 lg:col-span-3 bg-card border-border/50" data-id="element-1527">
          <CardContent className="p-5" data-id="element-1528">
            <div className="flex items-center gap-2 mb-2" data-id="element-1529">
              <MessageSquare className="size-4 text-primary" data-id="element-1530" />
              <p className="font-mono text-[10px] uppercase text-muted-foreground" data-id="element-1531">
                Media Amplification
              </p>
            </div>
            <p className="font-mono text-3xl font-bold text-foreground" data-id="element-1532">
              {outcome.reach}
            </p>
            <p className="font-mono text-[9px] uppercase text-muted-foreground mt-3 mb-1" data-id="element-1533">
              Top Platform
            </p>
            <div className="space-y-1" data-id="element-1534">
              {outcome.platforms.map((p, i) => <div key={p} className="flex items-center justify-between text-[10px] font-mono" data-id="element-1535">
                  <span className="text-foreground" data-id="element-1536">{p}</span>
                  <span className="text-muted-foreground" data-id="element-1537">
                    {[42, 28, 18][i] || 10}%
                  </span>
                </div>)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ROW 4: Comparison */}
      <Card className="bg-card border-border/50" data-id="element-1538">
        <CardHeader className="pb-3 pt-4 px-5 border-b border-border/50" data-id="element-1539">
          <CardTitle className="font-mono text-sm uppercase tracking-wider" data-id="element-1540">
            Perbandingan Skenario
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0" data-id="element-1541">
          <div className="overflow-x-auto" data-id="element-1542">
            <table className="w-full text-left" data-id="element-1543">
              <thead className="bg-muted/20 border-b border-border/50" data-id="element-1544">
                <tr data-id="element-1545">
                  <th className="font-mono text-[10px] uppercase text-muted-foreground p-3" data-id="element-1546">
                    Skenario
                  </th>
                  <th className="font-mono text-[10px] uppercase text-muted-foreground p-3" data-id="element-1547">
                    Sentimen
                  </th>
                  <th className="font-mono text-[10px] uppercase text-muted-foreground p-3" data-id="element-1548">
                    Eskalasi %
                  </th>
                  <th className="font-mono text-[10px] uppercase text-muted-foreground p-3" data-id="element-1549">
                    Risiko Massa
                  </th>
                  <th className="font-mono text-[10px] uppercase text-muted-foreground p-3" data-id="element-1550">
                    Media Reach
                  </th>
                  <th className="font-mono text-[10px] uppercase text-muted-foreground p-3 text-right" data-id="element-1551">
                    Skor Strategis
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50" data-id="element-1552">
                {comparisonRows.map(row => {
                const o = outcomes[row.id];
                const isActive = row.id === activeScenario;
                return <tr key={row.id} className={isActive ? 'bg-primary/5' : 'hover:bg-muted/20'} data-id="element-1553">
                      <td className="p-3 text-xs font-bold text-foreground" data-id="element-1554">
                        {row.name}
                      </td>
                      <td className="p-3 text-xs font-mono" data-id="element-1555">{o.sentiment}</td>
                      <td className="p-3 text-xs font-mono" data-id="element-1556">{o.escalation}%</td>
                      <td className="p-3" data-id="element-1557">
                        <Badge variant="outline" className="font-mono text-[9px] h-4" data-id="element-1558">
                          {o.risk}
                        </Badge>
                      </td>
                      <td className="p-3 text-xs font-mono" data-id="element-1559">{o.reach}</td>
                      <td className="p-3 text-right" data-id="element-1560">
                        <span className={`font-mono text-sm font-bold ${o.score >= 70 ? 'text-emerald-500' : o.score >= 50 ? 'text-amber-500' : 'text-red-500'}`} data-id="element-1561">
                          {o.score}/100
                        </span>
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
          <p className="px-5 py-3 font-mono text-[10px] text-muted-foreground border-t border-border/50" data-id="element-1562">
            Skor strategis tertinggi:{' '}
            <span className="text-emerald-500 font-bold" data-id="element-1563">
              Statement Presiden Langsung (78/100)
            </span>{' '}
            — rekomendasi AI
          </p>
        </CardContent>
      </Card>

      {/* ROW 5: AI Recommendation */}
      <Card className="bg-primary/5 border-primary/30" data-id="element-1564">
        <CardContent className="p-5" data-id="element-1565">
          <div className="flex items-center gap-2 mb-3" data-id="element-1566">
            <Sparkles className="size-5 text-primary" data-id="element-1567" />
            <h3 className="font-mono text-sm uppercase tracking-wider text-primary font-bold" data-id="element-1568">
              Rekomendasi AI Berdasarkan Simulasi
            </h3>
          </div>
          <ul className="space-y-2.5 mb-5" data-id="element-1569">
            <li className="flex items-start gap-2 text-xs text-foreground/90" data-id="element-1570">
              <Sparkles className="size-3 text-primary shrink-0 mt-0.5" data-id="element-1571" />
              Hindari skenario "diam 24 jam" — risiko eskalasi ke RED{' '}
              <strong data-id="element-1572">64-85%</strong>.
            </li>
            <li className="flex items-start gap-2 text-xs text-foreground/90" data-id="element-1573">
              <Sparkles className="size-3 text-primary shrink-0 mt-0.5" data-id="element-1574" />
              <strong data-id="element-1575">Statement Presiden langsung dalam 4 jam</strong>{' '}
              memberikan skor strategis tertinggi (78/100).
            </li>
            <li className="flex items-start gap-2 text-xs text-foreground/90" data-id="element-1576">
              <Sparkles className="size-3 text-primary shrink-0 mt-0.5" data-id="element-1577" />
              Aktifkan protokol Kominfo paralel untuk redam amplifikasi{' '}
              <strong data-id="element-1578">(efektivitas 71%)</strong>.
            </li>
            <li className="flex items-start gap-2 text-xs text-foreground/90" data-id="element-1579">
              <Sparkles className="size-3 text-primary shrink-0 mt-0.5" data-id="element-1580" />
              Komunikasi lokal di 3 wilayah hotspot (Jakarta, Surabaya, Bandung)
              wajib dalam 8 jam.
            </li>
          </ul>
          <p className="text-xs italic text-foreground/70 leading-relaxed mb-4 border-l-2 border-primary/30 pl-3" data-id="element-1581">
            {outcome.aiNote}
          </p>
          <div className="flex flex-wrap gap-2" data-id="element-1582">
            <Button size="sm" onClick={() => toast.success('Disimpan ke Brief Presiden')} data-id="element-1583">
              Simpan ke Brief Presiden
            </Button>
            <Button size="sm" onClick={() => setCompareOpen(true)} data-id="element-1584">
              Bandingkan dengan Simulasi Lain
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={compareOpen} onOpenChange={setCompareOpen} data-id="element-1585">
        <DialogContent className="max-w-4xl" data-id="element-1586">
          <DialogHeader data-id="element-1587">
            <DialogTitle data-id="element-1588">Perbandingan Skenario Krisis</DialogTitle>
            <DialogDescription data-id="element-1589">
              Side-by-side analisis konsekuensi dari 4 skenario simulasi. Skor
              tertinggi direkomendasikan AI.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1590">
            {comparisonRows.map(row => {
            const o = outcomes[row.id];
            const isBest = o.score === Math.max(...comparisonRows.map(r => outcomes[r.id].score));
            return <div key={row.id} className={`p-4 rounded-lg border-2 ${isBest ? 'border-emerald-500 bg-emerald-500/5' : 'border-border/50 bg-card'}`} data-id="element-1591">
                  <div className="flex items-center justify-between mb-3" data-id="element-1592">
                    <h4 className="font-bold text-sm" data-id="element-1593">{row.name}</h4>
                    {isBest && <Badge variant="outline" className="font-mono text-[9px] h-5 bg-emerald-500/10 text-emerald-600 border-emerald-500/30" data-id="element-1594">
                        ★ REKOMENDASI AI
                      </Badge>}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-1595">
                    <div className="p-2 rounded bg-muted/20 border border-border/50" data-id="element-1596">
                      <p className="font-mono text-[9px] text-muted-foreground uppercase" data-id="element-1597">
                        Sentimen
                      </p>
                      <p className={`font-mono font-bold mt-0.5 ${o.sentimentNum < 0 ? 'text-red-500' : 'text-emerald-500'}`} data-id="element-1598">
                        {o.sentiment}
                      </p>
                    </div>
                    <div className="p-2 rounded bg-muted/20 border border-border/50" data-id="element-1599">
                      <p className="font-mono text-[9px] text-muted-foreground uppercase" data-id="element-1600">
                        Eskalasi
                      </p>
                      <p className={`font-mono font-bold mt-0.5 ${o.escalation >= 60 ? 'text-red-500' : o.escalation >= 40 ? 'text-amber-500' : 'text-emerald-500'}`} data-id="element-1601">
                        {o.escalation}%
                      </p>
                    </div>
                    <div className="p-2 rounded bg-muted/20 border border-border/50" data-id="element-1602">
                      <p className="font-mono text-[9px] text-muted-foreground uppercase" data-id="element-1603">
                        Risiko Massa
                      </p>
                      <p className="font-mono font-bold mt-0.5" data-id="element-1604">{o.risk}</p>
                    </div>
                    <div className="p-2 rounded bg-muted/20 border border-border/50" data-id="element-1605">
                      <p className="font-mono text-[9px] text-muted-foreground uppercase" data-id="element-1606">
                        Reach
                      </p>
                      <p className="font-mono font-bold mt-0.5" data-id="element-1607">{o.reach}</p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-border/50" data-id="element-1608">
                    <div className="flex items-center justify-between mb-1" data-id="element-1609">
                      <span className="font-mono text-[9px] text-muted-foreground uppercase" data-id="element-1610">
                        Skor Strategis
                      </span>
                      <span className={`font-mono text-sm font-bold ${o.score >= 70 ? 'text-emerald-500' : o.score >= 50 ? 'text-amber-500' : 'text-red-500'}`} data-id="element-1611">
                        {o.score}/100
                      </span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden" data-id="element-1612">
                      <div className={`h-full ${o.score >= 70 ? 'bg-emerald-500' : o.score >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                    width: `${o.score}%`
                  }} data-id="element-1613" />
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground italic leading-relaxed mt-3" data-id="element-1614">
                    {o.aiNote}
                  </p>
                </div>;
          })}
          </div>
          <DialogFooter data-id="element-1615">
            <Button variant="outline" onClick={() => setCompareOpen(false)} data-id="element-1616">
              Tutup
            </Button>
            <Button onClick={() => {
            setActiveScenario('s2');
            setCompareOpen(false);
            toast.success('Skenario rekomendasi AI diaktifkan');
          }} data-id="element-1617">
              Aktifkan Rekomendasi AI
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>;
}